import { HttpErrorResponse, HttpEventType } from "@angular/common/http";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { ChatAudit } from "@common/ADAPT.Common.Model/organisation/chat-audit";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { AiModel } from "@common/lib/chat-ai/ai-model-pricing";
import { AiPricingService } from "@common/lib/chat-ai/ai-pricing.service";
import { DefaultSystemMessageWithMarkdown, IChatAttachmentBindingModel, IChatResponseBindingModel, MarkdownParser } from "@common/lib/chat-ai/chat-ai.service";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { StringUtilities } from "@common/lib/utilities/string-utilities";
import { ShellUiService } from "@common/shell/shell-ui.service";
import { ThemeService } from "@common/shell/theme.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { loadMessages } from "devextreme/localization";
import dxButton from "devextreme/ui/button";
import { Alert, Attachment, Message, MessageEnteredEvent, User } from "devextreme/ui/chat";
import dxScrollView from "devextreme/ui/scroll_view";
import { DxChatComponent } from "devextreme-angular";
import { DxChatTypes } from "devextreme-angular/ui/chat";
import { DxFileUploaderTypes } from "devextreme-angular/ui/file-uploader";
import { ParsedResponseStreamEvent } from "openai/lib/responses/EventTypes";
import { parseServerSentEvents } from "parse-sse";
import { auditTime, BehaviorSubject, catchError, debounceTime, EMPTY, lastValueFrom, Observable, Subject, Subscription } from "rxjs";
import { CHAT_AI_ORG_PROVIDER, IChatAiOrgProvider, IMegaPromptLabel } from "../chat-ai-org-provider.interface";
import { ChatAiUiService, ChatLocationMode, IChat, IChatHistoryEntry } from "../chat-ai-ui.service";
import { ChatResponseAbortedError } from "../chat-response-aborted-error";
import { ClientToolCallHandlerService } from "../client-toolcall-handler.service";
import { ConversationListComponent } from "../conversation-list/conversation-list.component";
import { MegaPromptMetadata, MegaPromptName } from "../mega-prompts";

enum ProgressType {
    Thinking = "thinking",
    FunctionCall = "functionCall",
}

interface IUploadFileResponse {
    fileId: string;
}

interface IUploadedFile extends File, Partial<IUploadFileResponse> {
    fileId?: string;
}

interface IUploadedChatAttachment extends IChatAttachmentBindingModel {
    file: File;
    signature: string;
}

export type ChatMessage = Message & {
    renderedMessage?: string,
    subtext?: string,
    completed: boolean,
    progressType?: ProgressType,
    isCopied?: boolean,
    isInterrupted?: boolean,
    isMegaPrompt?: boolean,
    isHistorical?: boolean,
};

// This will need to match the ExecuteMegaPromptPrefix string literal constant in AiChatServiceController.cs
const MegaPromptPrefix = "Execute mega prompt:";

const ContinuableErrorMessageFragments = [
    "unsupported_file",
    "invalid_request_error: invalid_value",
];

// Function call names that will be handled differently
enum FunctionCallName {
    QueryRepositoryEntities = "QueryRepositoryEntities",
    DiscoverEntities = "DiscoverEntities",
    LoadResourceOnDemand = "LoadResourceOnDemand",
}

@Component({
    selector: "adapt-chat-ai",
    standalone: false,
    templateUrl: "./chat-ai.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./chat-ai.component.scss"],
})
export class ChatAiComponent extends BaseComponent implements OnDestroy, AfterViewInit {
    @ViewChild(DxChatComponent) public chatComponent!: DxChatComponent;
    @ViewChild(ConversationListComponent) public conversationListComponent?: ConversationListComponent;

    @Input() public showAvatar = true;
    @Input() public showCost = false;
    @Output() public totalCostChange = new EventEmitter<number>();

    public readonly ChatLocationMode = ChatLocationMode;
    public chat!: IChat;

    public user: User = {
        id: "user",
    };

    private assistant: User = {
        id: "assistant",
        name: "Lumia Assistant",
        avatarUrl: "/favicon.png",
    };

    public readonly CopyButtonIcon = "copy";
    public readonly CopiedButtonIcon = "check";
    // These are the extensions allowed by OpenAI - raw as InputFilePart without conversion
    // public readonly AllowedChatFileExtensions = [
    //     ".art", ".bat", ".brf", ".c", ".cls", ".cpp", ".cs", ".css", ".csv", ".diff", ".doc", ".docx", ".dot", ".eml", ".es", ".gif", ".go", ".h", ".hs", ".htm",
    //     ".html", ".hwp", ".hwpx", ".ics", ".ifb", ".java", ".jpeg", ".jpg", ".js", ".json",
    //     ".keynote", ".ksh", ".ltx", ".mail", ".markdown", ".md", ".mht", ".mhtml", ".mjs", ".nws", ".odt", ".pages", ".patch", ".pdf",
    //     ".php", ".pkl", ".pl", ".pm", ".png", ".pot", ".ppa", ".pps", ".ppt", ".pptx", ".pwz", ".py", ".rb", ".rst", ".rtf",
    //     ".scala", ".sh", ".shtml", ".sql", ".srt", ".sty", ".svg", ".svgz", ".tar", ".tex", ".text", ".ts", ".txt", ".vcf", ".vtt",
    //     ".webp", ".wiz", ".xla", ".xlb", ".xlc", ".xlm", ".xls", ".xlsx", ".xlt", ".xlw", ".xml", ".yaml", ".yml", ".zip",
    // ];

    // Azure OpenAI only allows .md, .pdf, .json as InputFilePart - but if attached as image or text, the following extensions will be allowed
    // - .xlsx and any office doc format not allowed anymore
    // - even .txt will get HTTP 400 (invalid_request_error: unsupported_file) Parameter: input The file type you uploaded is not supported from Azure OpenAI
    // - but they appeared to be fine if base64 encoded (which will be done on the server side - not needed if using openai directly)
    //   (office formats are not)
    public readonly AllowedChatFileExtensions = [
        ".bat", ".c", ".cls", ".cpp", ".cs", ".css", ".csv", ".diff", ".eml", ".es", ".gif", ".go", ".h", ".hs", ".htm",
        ".html", ".ics", ".ifb", ".java", ".jpeg", ".jpg", ".js", ".json",
        ".ksh", ".ltx", ".mail", ".markdown", ".md", ".mht", ".mhtml", ".mjs", ".nws", ".patch", ".pdf",
        ".php", ".pl", ".pm", ".png", ".py", ".rb", ".rst",
        ".sh", ".shtml", ".sql", ".srt", ".sty", ".svg", ".svgz", ".tex", ".text", ".ts", ".txt",
        ".webp", ".xml", ".yaml", ".yml",
    ];
    public readonly AcceptedChatFileTypes = this.AllowedChatFileExtensions.join(",");
    public readonly MaxAttachmentSizeBytes = 5 * 1024 * 1024; // 5 MB

    public isDisabled = false;
    public totalCost = 0;
    public isStreaming = false;
    public applicableMegaPrompts: IMegaPromptLabel[] = [];
    public isConversationPopoverVisible = false;
    public latestConversation?: ChatAudit;

    // Queue for debounced push operations, keyed by message.id
    private messageUpdateQueue = new Map<string, { type: "update" | "remove"; data: ChatMessage }>();
    private messageUpdateQueueSubject = new Subject<void>();

    private readonly DiscoverEntitiesMessage = this.getProgressTextForFunctionCall(FunctionCallName.DiscoverEntities);
    private readonly LoadResourceOnDemandMessage = this.getProgressTextForFunctionCall(FunctionCallName.LoadResourceOnDemand);

    private lastScrollOffset?: number;
    private triggerScrollLastUserMessageToTop = new Subject<boolean>();
    private visibleChangeSubscription?: Subscription;
    private uploadedAttachmentsBySignature = new Map<string, IUploadedChatAttachment[]>();

    private get scrollView() {
        const el = this.elementRef?.nativeElement.querySelector(".dx-chat .dx-scrollable");
        return dxScrollView.getInstance(el) as dxScrollView;
    }

    constructor(
        @Inject(CHAT_AI_ORG_PROVIDER) private orgService: IChatAiOrgProvider,
        private dialogService: AdaptCommonDialogService,
        // private sseClient: SseClient,
        private clientToolCallHandlerService: ClientToolCallHandlerService,
        public chatAiUiService: ChatAiUiService,
        public shellUiService: ShellUiService,
        private aiPricingService: AiPricingService,
        elementRef: ElementRef,
        public themeService: ThemeService,
    ) {
        super(elementRef);

        loadMessages({
            en: {
                "dxChat-textareaPlaceholder": "Ask Lumia Assistant...",
            },
        });

        const persistedChat = this.chatAiUiService.getPersistedChat();
        if (persistedChat) {
            this.chat = persistedChat.chat;
            this.totalCost = persistedChat.totalCost;
        } else {
            this.createNewChatInternal();
        }

        // Debounced push queue: update/remove operations are queued per message.id and flushed every 200ms.
        // Call flushMessageUpdateQueue() to bypass the delay and dispatch immediately (e.g. on stream completion).
        this.messageUpdateQueueSubject.pipe(
            auditTime(200),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.flushMessageUpdateQueue());

        this.triggerScrollLastUserMessageToTop.pipe(
            auditTime(500),
            this.takeUntilDestroyed(),
        ).subscribe((shouldScroll) => {
            if (shouldScroll) {
                this.scrollLastUserMessageViewToTop();
            }
        });

        this.chatAiUiService.tabClearedEvent$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.chat = undefined as any;
            this.totalCost = 0;
            this.createNewChatInternal();
        });

        this.chatAiUiService.queuedMegaPromptExecution$.pipe(
            debounceTime(500),
            this.takeUntilDestroyed(),
        ).subscribe((prompt: IMegaPromptLabel) => {
            this.chatAiUiService.clearMegaPromptExecutionQueue();
            this.processMegaPrompt(prompt);
        });

        this.chatAiUiService.applicableMegaPromptLabels$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((labels) => this.applicableMegaPrompts = labels);
    }

    public ngOnDestroy() {
        this.flushMessageUpdateQueue();
        super.ngOnDestroy();

        this.chatAiUiService.persistChat(this.chat, this.totalCost);
    }

    public ngAfterViewInit() {
        this.chatAiUiService.responseStreaming$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((isStreaming) => {
            setTimeout(() => {
                // do this in the next digest cycle as this is after view init and subscribing to a behaviour subject is non-blocking
                // which would otherwise cause ExpressionChangedAfterItHasBeenCheckedError
                this.setStreaming(isStreaming);
                if (!isStreaming) {
                    const currentPersistedChat = this.chatAiUiService.getPersistedChat();
                    if (currentPersistedChat?.chat === this.chat) {
                        this.totalCost = currentPersistedChat.totalCost;
                    }
                }

                this.totalCostChange.emit(this.totalCost);
            });
        });

        ElementUtilities.waitUntilPositionStable(this.elementRef?.nativeElement).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.scrollMessageListToBottom();
            setTimeout(() => this.chatComponent.instance.focus(), 300);
            this.visibleChangeSubscription?.unsubscribe();
            this.visibleChangeSubscription = this.chatAiUiService.chatVisibleChange$.pipe(
                this.takeUntilDestroyed(),
            ).subscribe(() => {
                setTimeout(() => this.chatComponent.instance.focus(), 300);
            });
        });

        this.updateLatestConversation();
    }

    public restoreLatestConversation() {
        if (!this.latestConversation) {
            return;
        }

        this.chatAiUiService.getConversationHistory(this.latestConversation.conversationId).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((history) => {
            const totalCost = this.aiPricingService.calculateTotalCost(this.latestConversation!);
            this.onConversationSwitched({ conversationId: this.latestConversation!.conversationId, history, totalCost });
        });
    }

    @Autobind
    public overrideSendMessageButtonClick() {
        if (this.isStreaming) {
            this.abortActiveResponseStream("Chat aborted by user.");
        }
    }

    public setStreaming(isStreaming: boolean) {
        this.isStreaming = isStreaming;
        // send button is not exposed by the dxChat component, so have to manually find it and bind to its
        // click event to trigger change detection for enabling/disabling the button based on the disabled state of the chat
        // send message button is recreated each time dxChat is repainted - i.e. they are different instances
        const sendMessageButton = (this.chatComponent?.instance as any)?._messageBox?._textArea?._sendButton as dxButton;
        if (sendMessageButton) {
            setTimeout(() => {
                const el = sendMessageButton.$element().get(0);
                if (el) {
                    if (this.isStreaming) {
                        sendMessageButton.option("icon", "far fa-circle-xmark fa-beat");
                        // can't even use onClick here as dxChat is blocking it when there is no input text
                        el.addEventListener("click", this.overrideSendMessageButtonClick);
                        el.removeAttribute("aria-disabled");
                        el.setAttribute("title", "Interrupt response streaming...");
                    } else {
                        // icon from here: https://github.com/DevExpress/DevExtreme/tree/25_2/packages/devextreme-scss/images/icons/fluent
                        sendMessageButton.option("icon", "arrowright");
                        el.removeEventListener("click", this.overrideSendMessageButtonClick);
                        el.setAttribute("aria-disabled", "true");
                        el.removeAttribute("title");
                    }
                }
            }, 100);
        }
    }

    public get alerts$(): Observable<Alert[]> {
        return this.chat.alertsSubject.asObservable();
    }

    public async onMessageEntered(event: MessageEnteredEvent) {
        if (this.chat.isDisabled || this.isStreaming) {
            return;
        }

        const preparedMessage = this.prepareMessageForSending(event.message);
        if (!preparedMessage) {
            this.alertError("One or more attachments could not be prepared. Please attach the files again and retry.");
            return;
        }

        // disable chat
        this.toggleDisabledState(true, event.event);

        // add users message
        // - id needs to be "string" as that will become dxChat internal message data which will be compared for === when updating
        // (this explains why user message couldn't been updated before)
        const chatMessage = { id: Date.now().toString(), ...preparedMessage, completed: true } as ChatMessage;
        this.updateStore("insert", chatMessage);

        if (!this.chat.alerts.length) {
            await this.processMessageSending(chatMessage, event.event);
        }
    }

    public async processMegaPrompt(prompt: IMegaPromptLabel) {
        if (this.chat.isDisabled || this.isStreaming) {
            return;
        }

        this.toggleDisabledState(true);
        const chatMessage = {
            id: Date.now().toString(),
            author: this.user,
            timestamp: new Date(),
            text: prompt.promptName,
            renderedMessage: prompt.label,
            completed: true,
            isMegaPrompt: true,
        } as ChatMessage;
        this.updateStore("insert", chatMessage);
        // since this is not coming from dxChat component, the above updateStore is still pushing into a cache
        // without actually inserted into the store - need the following so it will prompt for confirmation on 'new'
        this.chat.dataSource.store().insert(chatMessage);

        if (!this.chat.alerts.length) {
            await this.processMessageSending(chatMessage);
        }
    }

    @Autobind
    public uploadFile(file: File, progressCallback?: unknown): Promise<IUploadFileResponse> {
        if (file.size > this.MaxAttachmentSizeBytes) {
            return Promise.reject(new Error(`File "${file.name}" exceeds the ${this.MaxAttachmentSizeBytes / (1024 * 1024)} MB size limit.`));
        }

        // dx can take a promise return type but not observable - wrap this in a promise
        return new Promise<IUploadFileResponse>((resolve, reject) => {
            this.chatAiUiService.uploadFile(file, file.name).subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        if (typeof progressCallback === "function") {
                            progressCallback(event.loaded);
                        }
                        return;
                    }

                    if (event.type !== HttpEventType.Response) {
                        return;
                    }

                    try {
                        const response = this.parseUploadFileResponse(event.body ?? "");
                        Object.assign(file as File & IUploadFileResponse, response);
                        this.trackUploadedAttachment(file, response.fileId);
                        if (typeof progressCallback === "function") {
                            progressCallback(file.size);
                        }
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error);
                },
            });
        });
    }

    public onAttachmentDownloadClick(e: DxChatTypes.AttachmentDownloadClickEvent) {
        const attachment = e.attachment as (Attachment & Partial<IChatAttachmentBindingModel>) | undefined;
        if (!attachment?.fileId) {
            this.alertError("This attachment cannot be downloaded because its file ID is missing.");
            return;
        }

        void this.downloadAttachment(attachment as Attachment & IChatAttachmentBindingModel);
    }

    @Autobind
    public onAttachmentValueChanged(e: DxFileUploaderTypes.ValueChangedEvent) {
        const selectedFiles = (e.value ?? []) as IUploadedFile[];
        const previousFiles = (e.previousValue ?? []) as IUploadedFile[];
        const validFiles = selectedFiles.filter((file) => this.isAllowedChatAttachment(file));
        if (validFiles.length !== selectedFiles.length) {
            // leave the invalid files in the chat input box so user can see they are not allowed and remove them if they want
            // Note that overriding chat value here (removing the invalid files) will cause dxChat to not able to enable the
            // send message button even after typing new message
            return;
        }

        // Disallow anything > 5MB (our input context is only 400k tokens, assuming 10 bytes per token on average, leave a little gap for prompt)
        const oversizedFiles = selectedFiles.filter((file) => file.size > this.MaxAttachmentSizeBytes);
        if (oversizedFiles.length > 0) {
            const names = oversizedFiles.map((f) => f.name).join(", ");
            this.alertError(`The following attachment(s) exceed the ${this.MaxAttachmentSizeBytes / (1024 * 1024)} MB size limit and cannot be uploaded: ${names}`, true);
            return;
        }

        this.chat.alertsSubject.next([]); // clear any existing alerts when sending a new message
        const currentFiles = new Set(validFiles);
        if (!this.isStreaming) {
            // only clean up attachment file when not streaming (when streaming, user request will be moved to previous which will also remove from currently attachment)
            const removedFiles = previousFiles.filter((file) => !currentFiles.has(file));
            for (const removedFile of removedFiles) {
                if (removedFile.fileId) {
                    this.deleteAttachmentFile(removedFile.fileId);
                }
            }
        }

        for (const [signature, uploadedAttachments] of this.uploadedAttachmentsBySignature.entries()) {
            const remainingAttachments = uploadedAttachments.filter((attachment) => currentFiles.has(attachment.file));
            if (remainingAttachments.length > 0) {
                this.uploadedAttachmentsBySignature.set(signature, remainingAttachments);
            } else {
                this.uploadedAttachmentsBySignature.delete(signature);
            }
        }
    }

    public async createNewChat(event: Event) {
        event.stopPropagation();

        const confirmChatClose = this.chat.store.length === 0
            || await lastValueFrom(this.dialogService.openConfirmationDialogWithBoolean({
                title: "Close chat",
                message: "Are you sure you wish to close the existing chat and start a new one?",
                confirmButtonText: "Close and start new chat",
                cancelButtonText: "Cancel",
            }), { defaultValue: true });

        if (confirmChatClose) {
            await this.cleanupExistingStreaming("Streaming aborted because the active chat was closed.");
            this.createNewChatInternal();
        }
    }

    public moveChatToDialog() {
        this.chatAiUiService.chatLocationMode = ChatLocationMode.ApplicationBarDialog;
    }

    public createNewChatInternal() {
        this.chatAiUiService.startNewChatGeneration();
        const chat = {
            output: [],
            store: [],
            dataSource: new DataSource({
                store: new CustomStore<ChatMessage, string>({
                    key: "id",
                    load: () => new Promise((resolve) => {
                        setTimeout(() => resolve([...chat.store]));
                    }),
                    insert: (message) => new Promise((resolve) => {
                        setTimeout(() => {
                            chat.store.push(message);
                            resolve(message);
                        });
                    }),
                    // The streaming pipeline coalesces chat-message updates through
                    // store().push([{ type: "update", data, key }, ...]) (see
                    // messageUpdateQueueSubject below). DevExtreme's CustomStore
                    // ignores push updates entirely when no update() handler is
                    // defined, which would surface as the first delta rendering but
                    // every subsequent batch silently disappearing. Mutate the
                    // backing array in place so future load() reads see the new text
                    // and renderedMessage.
                    update: (key, message) => new Promise<void>((resolve) => {
                        setTimeout(() => {
                            const index = chat.store.findIndex((m) => m.id === key);
                            if (index !== -1) {
                                chat.store[index] = { ...chat.store[index], ...message };
                            }
                            resolve();
                        });
                    }),
                    remove: (key) => new Promise<void>((resolve) => {
                        setTimeout(() => {
                            const index = chat.store.findIndex((m) => m.id === key);
                            if (index !== -1) {
                                chat.store.splice(index, 1);
                            }
                            resolve();
                        });
                    }),
                }),
                paginate: false,
            }),
            isDisabled: false,
            alerts: [],
            alertsSubject: new BehaviorSubject([]),
        } as IChat;

        this.chat = chat;
        this.totalCost = 0;
        this.chatAiUiService.persistChat(this.chat, this.totalCost);
        this.totalCostChange.emit(this.totalCost);
    }

    public executeMegaPrompt(prompt: IMegaPromptLabel) {
        this.chatAiUiService.queueMegaPromptExecution(prompt);
    }

    private async cleanupExistingStreaming(abortMessage: string) {
        if (this.chatAiUiService.isResponseStreaming) {
            this.abortActiveResponseStream(abortMessage);
            await this.chatAiUiService.waitForActiveResponseToFinish();
        }
    }

    /**
     * Polyfill for `ReadableStream[Symbol.asyncIterator]` not being supported in Safari.
     *
     * Ideally would just do the following:
     *   `for await (const streamEvent of parseServerSentEvents(response))`
     */
    private async* iterReadableStream<T>(stream: ReadableStream<T>) {
        const reader = stream.getReader();
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    return;
                }

                yield value;
            }
        } finally {
            reader.releaseLock();
        }
    }

    private async processMessageSending(userMessage: ChatMessage, sourceEvent?: Event) {
        const responseChatGeneration = this.chatAiUiService.currentChatGeneration;
        const responseIsForCurrentChat = () => this.chatAiUiService.currentChatGeneration === responseChatGeneration;
        let progressMessage: ChatMessage | undefined = undefined;
        let message: ChatMessage | undefined = undefined;
        const lastPreviousResponseId = this.chat.previousResponseId;
        this.chat.alertsSubject.next([]); // clear any existing alerts when sending a new message

        const body: IChatResponseBindingModel = {
            organisationId: this.orgService.getOrganisationId(),
            systemChatMessage: DefaultSystemMessageWithMarkdown + " Make sure to respond with valid Markdown syntax for tables, etc.",
            userChatMessage: userMessage.text ?? "",
            conversationId: this.chat.conversationId,
            previousResponseId: this.chat.previousResponseId,
            attachments: this.getRequestAttachments(userMessage.attachments),
        };
        if (userMessage.isMegaPrompt) {
            body.megaPromptName = userMessage.text as MegaPromptName;
        }

        try {
            await this.chatAiUiService.streamResponse(body, async (response) => {
                // this should just be the following:
                //  for await (const streamEvent of parseServerSentEvents(response))
                // however, this is not supported in Safari. have to manually read the reader.
                for await (const streamEvent of this.iterReadableStream(parseServerSentEvents(response))) {
                    if (streamEvent.data === "[DONE]") {
                        break;
                    }

                    if (!responseIsForCurrentChat()) {
                        continue;
                    }

                    // Handle custom conversation.created event from the server (sent on first message only)
                    if (streamEvent.type === "conversation.created") {
                        const conversationData = JSON.parse(streamEvent.data) as { conversationId: string };
                        this.chat.conversationId = conversationData.conversationId;
                        continue;
                    }

                    if (streamEvent.type === "processing.attachment") {
                        const attachmentData = JSON.parse(streamEvent.data) as { attachmentName: string };
                        const truncatedName = attachmentData.attachmentName.length > 16
                            ? `${attachmentData.attachmentName.substring(0, 16)}`
                            : attachmentData.attachmentName;
                        progressMessage = this.insertProgressMessage(progressMessage, `Processing attachment: ${truncatedName}...`);
                        continue;
                    }

                    if (streamEvent.type === "processing.request") {
                        progressMessage = this.insertProgressMessage(progressMessage, "Waiting for AI response...");
                        continue;
                    }

                    const event = JSON.parse(streamEvent.data) as ParsedResponseStreamEvent;
                    if (event.type === "error") {
                        throw event;
                    }

                    if (event.type === "response.created") {
                        // don't backup previousResponseId here as we only want it to fallback to the id when the user message is sent, not previous tool calls after that
                        this.chat.previousResponseId = event.response.id;
                        this.chat.output = [];
                        progressMessage = this.insertProgressMessage(progressMessage, "Thinking...");
                    }

                    if (event.type === "response.output_item.added") {
                        this.chat.output.splice(event.output_index, 0, event.item);

                        if (progressMessage) {
                            if (event.item.type === "function_call") {
                                progressMessage.progressType = ProgressType.FunctionCall;
                                progressMessage.text = this.getProgressTextForFunctionCall(event.item.name) + "...";
                                progressMessage.subtext = undefined;
                                this.updateAssistantMessage(progressMessage);
                            } else if (event.item.type !== "message") {
                                progressMessage.progressType = ProgressType.Thinking;
                                progressMessage.text = "Thinking...";
                                progressMessage.subtext = undefined;
                                this.updateAssistantMessage(progressMessage);
                            } else if (event.item.type === "message") {
                                // started outputting message, we can remove the progress message now...
                                this.removeAssistantMessage(progressMessage);
                                progressMessage = undefined;
                            }
                        }
                    }

                    if (progressMessage && event.type === "response.function_call_arguments.done" && event.arguments) {
                        const functionArgs = JSON.parse(event.arguments);
                        if (functionArgs.entityType && typeof functionArgs.entityType === "string") {
                            const entityTypeWithoutNamespace = (functionArgs.entityType as string).split(".").pop();
                            const withSpaces = StringUtilities.addSpacesToPascalCase(entityTypeWithoutNamespace ?? "");
                            if (withSpaces) {
                                progressMessage.text = "Retrieving entities...";
                                progressMessage.subtext = withSpaces.toLocaleLowerCase();
                                this.updateAssistantMessage(progressMessage);
                            }
                        } else if (progressMessage.progressType === ProgressType.FunctionCall &&
                            (functionArgs.resourceName && typeof functionArgs.resourceName === "string" ||
                                functionArgs.query && typeof functionArgs.query === "string")) {
                            if (progressMessage.text.includes(this.DiscoverEntitiesMessage)) {
                                progressMessage.text = "Discovering entities...";
                            } else if (progressMessage.text.includes(this.LoadResourceOnDemandMessage)) {
                                progressMessage.text = "Loading resource...";
                            } else {
                                progressMessage.text = "Searching...";
                            }

                            progressMessage.subtext = functionArgs.query ?? functionArgs.resourceName;
                            this.updateAssistantMessage(progressMessage);
                        }
                    }

                    if (event.type === "response.output_item.done") {
                        Object.assign(this.chat.output[event.output_index], event.item);
                        this.clientToolCallHandlerService.onOutputItemDone(event);
                    }

                    if (event.type === "response.content_part.added" || event.type === "response.content_part.done") {
                        const messageResponse = this.chat.output[event.output_index];
                        if (messageResponse.type === "message" && event.part.type !== "reasoning_text") {
                            // add content to recorded output so we can update it in delta
                            messageResponse.content.splice(event.content_index, 0, event.part);

                            if (this.chat.previousResponseId) {
                                const text = event.part.type === "output_text"
                                    ? event.part.text
                                    : event.part.refusal;
                                if (!message) {
                                    message = this.insertAssistantMessage(text);
                                } else {
                                    message.text = text;
                                }
                                this.updateAssistantMessage(message);
                            }
                        }
                    }

                    if (event.type === "response.output_text.delta" || event.type === "response.output_text.done") {
                        const responseOutput = this.chat.output[event.output_index];
                        if (responseOutput.type === "message" && message) {
                            const content = responseOutput.content[event.content_index];
                            if (content.type === "output_text") {
                                if (event.type === "response.output_text.delta") {
                                    message.text += event.delta;
                                } else {
                                    message.text = event.text;
                                }

                                this.updateAssistantMessage(message);
                            }
                        }

                        if (event.type === "response.output_text.delta" && message) {
                            this.triggerScrollLastUserMessageToTop.next(true);
                        } else if (message) {
                            this.triggerScrollLastUserMessageToTop.next(false);
                            this.lastScrollOffset = undefined;
                        }
                    }

                    if (event.type === "response.incomplete" && event.response.incomplete_details) {
                        const errorMessage = event.response.incomplete_details.reason === "max_output_tokens"
                            ? "You have reached the context limit. Please start a new chat."
                            : "The response was interrupted by the AI's content filter. You can retry or ask why it was filtered.";
                        this.alertError(errorMessage, event.response.incomplete_details.reason === "content_filter");

                        if (progressMessage) {
                            this.removeAssistantMessage(progressMessage);
                            progressMessage = undefined;
                        }

                        if (message) {
                            this.completeChatMessage(message);
                        }
                    }

                    if (event.type === "response.completed") {
                        if (message) {
                            this.completeChatMessage(message);
                        }

                        // Flush any remaining queued updates so the final message renders immediately
                        this.flushMessageUpdateQueue();

                        // use this so don't have to change it every time we change the model
                        const currentCost = this.aiPricingService.calculateTotalCostForModel(
                            this.aiPricingService.getAiModel(event.response.model) as AiModel,
                            event.response.usage?.input_tokens ?? 0,
                            event.response.usage?.input_tokens_details.cached_tokens ?? 0,
                            event.response.usage?.output_tokens ?? 0,
                            event.response.usage?.output_tokens_details.reasoning_tokens ?? 0);
                        this.totalCost += currentCost;
                        this.totalCostChange.emit(this.totalCost);
                        this.chatAiUiService.persistChat(this.chat, this.totalCost);
                    }
                }
            });
        } catch (error: HttpErrorResponse | any) {
            if (!responseIsForCurrentChat()) {
                return;
            }

            if (error instanceof ChatResponseAbortedError) {
                this.alertError(error.message, true);
            } else {
                this.log.error("Chat error", error);
                const streamErrorData = error?.data ?? error?.value?.data;
                let alertMessage: string;
                if (typeof streamErrorData === "string") {
                    try {
                        const data = JSON.parse(streamErrorData) as { message?: string, error?: { message?: string }, data?: { message?: string } };
                        alertMessage = data.message ??
                            data.error?.message ??
                            data.data?.message ??
                            ErrorHandlingUtilities.getHttpResponseMessage(error?.error ?? error);
                    } catch {
                        alertMessage = ErrorHandlingUtilities.getHttpResponseMessage(error?.error ?? error);
                    }
                } else {
                    alertMessage = ErrorHandlingUtilities.getHttpResponseMessage(error?.error ?? error);
                }
                this.alertError(alertMessage, this.alertMessageCanContinue(alertMessage));
            }

            // mark the user message as interrupted to indicate the conversation was interrupted at this message
            const interruptedMessage = message as ChatMessage | undefined;
            if (interruptedMessage) {
                // this is for response already start streaming - interrupt on response
                interruptedMessage.isInterrupted = true;
                interruptedMessage.text += " ";
                this.updateStore("update", interruptedMessage);
            } else {
                // this is before response start -> interrupt on the user message
                userMessage.isInterrupted = true;
                userMessage.text += " ";
                this.updateStore("update", userMessage);
            }

            this.chat.previousResponseId = lastPreviousResponseId; // revert to previous response id so that user can continue the conversation from the last successful response
            this.chat.isDisabled = this.chat.alerts.length > 0;
        } finally {
            if (responseIsForCurrentChat()) {
                if (progressMessage) {
                    this.removeAssistantMessage(progressMessage);
                    progressMessage = undefined;
                }

                const incompleteMessage = message as ChatMessage | undefined;
                if (incompleteMessage && !incompleteMessage.completed) {
                    this.completeChatMessage(incompleteMessage);
                }

                this.toggleDisabledState(this.chat.alerts.length > 0, sourceEvent);
                this.flushMessageUpdateQueue();
            }
        }
    }

    public convertToHtml(message: ChatMessage): string {
        const renderedMessage = MarkdownParser.processSync(message.text).toString();
        message.renderedMessage = renderedMessage;
        return renderedMessage;
    }

    public onCopyButtonClick(message: ChatMessage) {
        navigator.clipboard?.writeText(message.text);

        message.isCopied = true;
        setTimeout(() => {
            message.isCopied = false;
        }, 2500);
    }

    public refreshConversationList() {
        this.conversationListComponent?.refreshConversations();
    }

    public updateLatestConversation() {
        this.chatAiUiService.getLatestConversation().pipe(
            this.takeUntilDestroyed(),
        ).subscribe((latestConversation) => this.latestConversation = latestConversation);
    }

    @Autobind
    public async onConversationSwitched(event: { conversationId: string, history: IChatHistoryEntry[], totalCost: number }) {
        await this.cleanupExistingStreaming("Chat switched before response completed.");
        this.chatAiUiService.startNewChatGeneration();
        this.chat.conversationId = event.conversationId;
        this.chat.previousResponseId = undefined;
        this.chat.store.splice(0, this.chat.store.length); // clear current messages
        for (const item of Array.isArray(event.history) ? event.history : []) {
            if (item.userMessage) {
                const userTimestamp = item.userMessageTimestamp;
                const parsed = this.parseAttachmentsFromMessage(item.userMessage);
                const megaPromptMatch = item.userMessage.match(new RegExp(`^${MegaPromptPrefix}\\s+(.+)$`));
                const chatMessage = {
                    id: `${userTimestamp.getTime()}-user`,
                    type: "text",
                    timestamp: userTimestamp,
                    author: this.user,
                    text: megaPromptMatch?.[1] ?? parsed?.strippedText ?? item.userMessage,
                    attachments: parsed?.attachments,
                    isHistorical: parsed?.hasOldAttachment,
                    completed: true,
                } as ChatMessage;
                if (megaPromptMatch) {
                    const megaPromptName = megaPromptMatch[1] as MegaPromptName;
                    chatMessage.renderedMessage = MegaPromptMetadata[megaPromptName].label;
                    chatMessage.isMegaPrompt = true;
                }
                this.chat.store.push(chatMessage);
            }

            if (item.assistantMessage) {
                const assistantTimestamp = item.timestamp;
                this.chat.store.push({
                    id: `${assistantTimestamp.getTime()}-assistant`,
                    type: "text",
                    timestamp: assistantTimestamp,
                    author: this.assistant,
                    text: item.assistantMessage,
                    completed: true,
                });
            }
        }

        this.totalCost = event.totalCost;
        this.chat.dataSource.load();
        setTimeout(() => this.chatComponent.instance.repaint(), 100);
        this.chatAiUiService.persistChat(this.chat, this.totalCost);
        this.isConversationPopoverVisible = false;
    }

    private alertMessageCanContinue(message: string) {
        return !!ContinuableErrorMessageFragments.find((fragment) => message.includes(fragment));
    }

    private alertError(message: string, canContinue = false) {
        const alert: Alert = { message: canContinue ? message : `${message} - Start a new chat.` };
        if (canContinue) {
            this.chat.alertsSubject.next([alert]);
        } else {
            this.chat.alerts.push(alert);
            this.chat.alertsSubject.next(this.chat.alerts);
        }
    }

    private toggleDisabledState(disabled: boolean, event?: Event) {
        this.chat.isDisabled = disabled;

        const target = event?.target;
        if (target instanceof HTMLElement) {
            if (disabled) {
                target.blur();
            } else {
                target.focus();
            }
        }
    };

    private insertProgressMessage(progressMessage: ChatMessage | undefined, text: string): ChatMessage {
        if (progressMessage) {
            progressMessage.text = text;
            progressMessage.subtext = undefined;
            this.updateAssistantMessage(progressMessage);
            return progressMessage;
        }
        return this.insertAssistantMessage(text, ProgressType.Thinking);
    }

    private insertAssistantMessage(text: string, progressType?: ProgressType) {
        const message: ChatMessage = {
            id: this.chat.previousResponseId! + (progressType ? "_progress" : ""),
            type: "text",
            timestamp: new Date(),
            author: this.assistant,
            completed: false,
            text,
            progressType,
        };

        this.convertToHtml(message);
        this.updateStore("insert", message);

        return message;
    }

    private updateAssistantMessage(message: ChatMessage) {
        this.updateStore("update", message);
    }

    private removeAssistantMessage(message: ChatMessage) {
        this.updateStore("remove", message);
    }

    private updateStore(type: "insert" | "update" | "remove", message: ChatMessage) {
        if (type === "insert") {
            // Insert always goes through immediately
            this.chat.dataSource.store().push([{ type, data: message, key: message.id?.toString() }]);
        } else {
            // Queue update/remove per message.id, flushed every 200ms (or immediately via flushMessageUpdateQueue)
            this.messageUpdateQueue.set(message.id!.toString(), { type, data: message });
            if (this.isDestroyed) {
                // The stream can continue after this component is destroyed. Its throttled queue
                // subscription has been disposed, so push directly to the shared data source used
                // by any replacement component.
                this.flushMessageUpdateQueue();
            } else {
                this.messageUpdateQueueSubject.next();
            }
        }
    }

    /**
     * Fast-track the message update queue to dispatch immediately, bypassing the auditTime(200) delay.
     * Call this when the stream completes to ensure the final rendered message appears without waiting.
     */
    private flushMessageUpdateQueue() {
        if (this.messageUpdateQueue.size === 0) {
            return;
        }

        const batch = Array.from(this.messageUpdateQueue.entries()).map(([key, item]) => {
            if (item.data.author?.id === this.assistant.id) {
                this.convertToHtml(item.data);
            }

            return { ...item, key };
        });
        this.messageUpdateQueue.clear();
        this.chat.dataSource.store().push(batch);
    }

    private getProgressTextForFunctionCall(pascalCaseName: string): string {
        if (pascalCaseName.includes(FunctionCallName.QueryRepositoryEntities)) {
            return "Querying database";
        } else {
            const withSpaces = StringUtilities.addSpacesToPascalCase(pascalCaseName);
            const withVerb = withSpaces.replace(/^Get /, "Getting ");
            return withVerb.charAt(0).toUpperCase() + withVerb.slice(1).toLowerCase();
        }
    }

    private completeChatMessage(message: ChatMessage) {
        message.completed = true;
        // need to change the text here or the final update will not trigger render without repaint()
        // which will scroll to the end. With this, it will stay where it is.
        // (tried trim(), copy but provided the text value is the same, it won't trigger update)
        message.text += " ";
        this.updateAssistantMessage(message);
        this.triggerScrollLastUserMessageToTop.next(true);
    }

    private scrollMessageListToBottom() {
        this.scrollView?.scrollTo({ top: this.scrollView.scrollHeight() });
    }

    private scrollLastUserMessageViewToTop() {
        const lastMessageGroup = this.elementRef?.nativeElement.querySelector(".dx-chat-messagegroup.dx-chat-last-messagegroup-alignment-end");
        if (lastMessageGroup) {
            const scrollView = this.scrollView;
            if (scrollView) {
                const offset = (lastMessageGroup as HTMLElement).offsetTop;
                const currentOffset = scrollView.scrollOffset().top;
                // Skip re-scrolling when the user has already manually scrolled below this message,
                // or when we're already at this message and the chat still has more content below it.
                if (currentOffset > offset || (offset === this.lastScrollOffset && offset + scrollView.clientHeight() < scrollView.scrollHeight())) {
                    return;
                }

                this.lastScrollOffset = offset;
                scrollView.scrollTo({ top: offset });
            }
        }
    }

    private parseUploadFileResponse(responseText: string): IUploadFileResponse {
        const response = JSON.parse(responseText) as Partial<IUploadFileResponse>;
        if (!response.fileId) {
            throw new Error("The upload completed without a fileId response.");
        }

        return response as IUploadFileResponse;
    }

    private prepareMessageForSending(message: Message): Message | undefined {
        const attachments = this.resolveMessageAttachments(message.attachments);
        if (message.attachments?.length && !attachments) {
            return undefined;
        }

        return {
            ...message,
            attachments,
        };
    }

    private resolveMessageAttachments(attachments?: Attachment[]): IChatAttachmentBindingModel[] | undefined {
        if (!attachments?.length) {
            return undefined;
        }

        const resolvedAttachments: IChatAttachmentBindingModel[] = [];
        for (const attachment of attachments) {
            const signature = this.getAttachmentSignature(attachment);
            const matchingAttachments = this.uploadedAttachmentsBySignature.get(signature);
            const resolvedAttachment = matchingAttachments?.shift();
            if (!resolvedAttachment) {
                return undefined;
            }

            if (!matchingAttachments?.length) {
                this.uploadedAttachmentsBySignature.delete(signature);
            }

            resolvedAttachments.push({
                fileId: resolvedAttachment.fileId,
                name: resolvedAttachment.name,
                size: resolvedAttachment.size,
            });
        }

        return resolvedAttachments;
    }

    private getRequestAttachments(attachments?: Attachment[]): IChatAttachmentBindingModel[] | undefined {
        const requestAttachments = attachments
            ?.filter((attachment): attachment is IChatAttachmentBindingModel => typeof attachment.fileId === "string" && attachment.fileId.length > 0)
            .map((attachment) => ({
                fileId: attachment.fileId,
                name: attachment.name,
                size: attachment.size,
            }));

        return requestAttachments?.length ? requestAttachments : undefined;
    }

    private abortActiveResponseStream(reason: string) {
        if (!this.chatAiUiService.isResponseStreaming) {
            return;
        }

        this.log.info(reason);
        this.chatAiUiService.abortActiveResponseStream(reason);
    }

    private trackUploadedAttachment(file: File, fileId: string) {
        const signature = this.getAttachmentSignature(file);
        const existingAttachments = this.uploadedAttachmentsBySignature.get(signature) ?? [];
        existingAttachments.push({
            file,
            fileId,
            name: file.name,
            size: file.size,
            signature,
        });
        this.uploadedAttachmentsBySignature.set(signature, existingAttachments);
    }

    private getAttachmentSignature(attachment: Pick<Attachment, "name" | "size">): string {
        return `${attachment.name}:${attachment.size}`;
    }

    private isAllowedChatAttachment(file: Pick<File, "name">): boolean {
        const fileName = file.name.toLowerCase();
        return this.AllowedChatFileExtensions.some((extension) => fileName.endsWith(extension));
    }

    private deleteAttachmentFile(fileId: string) {
        this.chatAiUiService.deleteAttachmentFile(fileId).pipe(
            catchError((error) => {
                this.log.error("Failed to delete chat attachment", ErrorHandlingUtilities.getHttpResponseMessage(error));
                return EMPTY;
            }),
        ).subscribe();
    }

    private downloadAttachment(attachment: Attachment & IChatAttachmentBindingModel) {
        this.chatAiUiService.downloadAttachmentFile(attachment.fileId).pipe(
            catchError((error) => {
                this.log.error("Attachment download failed", error);
                this.alertError(ErrorHandlingUtilities.getHttpResponseMessage((error as HttpErrorResponse)?.error ?? error));
                return EMPTY;
            }),
        ).subscribe((response) => {
            const blob = response.body;
            if (!blob) {
                this.log.error("Attachment download failed", "The download completed without a file payload.");
                this.alertError("The attachment download completed without a file payload.");
                return;
            }

            const fileName = this.getDownloadFileName(response.headers.get("Content-Disposition")) ?? attachment.name;
            this.triggerBrowserDownload(blob, fileName);
        });
    }

    private getDownloadFileName(contentDisposition: string | null): string | undefined {
        if (!contentDisposition) {
            return undefined;
        }

        // RFC 5987 / RFC 6266 form used for UTF-8 filenames, typically percent-encoded.
        // Prefer this first because servers may send both filename* and filename, and filename*
        // preserves non-ASCII characters more reliably.
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1]);
        }

        // Common quoted fallback for filenames containing spaces or punctuation.
        const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
        if (quotedMatch?.[1]) {
            return quotedMatch[1];
        }

        // Plain unquoted fallback used by some older or simpler servers.
        const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
        return plainMatch?.[1]?.trim();
    }

    private triggerBrowserDownload(blob: Blob, fileName: string) {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        window.document.body.append(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
    }

    private parseAttachmentsFromMessage(message: string): { strippedText: string; attachments: Attachment[]; hasOldAttachment: boolean } | null {
        const match = message.match(/(?:\s+)?\[attachments:\s+(.+?)\]\s*$/);
        if (!match) return null;

        // Split by comma while respecting parentheses groups (new format: "name (size: N, id: ID)" contains a comma inside parens)
        const entries = match[1].split(",").reduce<string[]>((acc, part) => {
            const current = acc.length > 0 ? `${acc.pop()},${part}` : part;
            const depth = (current.match(/\(/g) ?? []).length - (current.match(/\)/g) ?? []).length;
            if (depth === 0) {
                acc.push(current.trim());
            } else {
                acc.push(current);
            }
            return acc;
        }, []);

        // New format: "name (size: N, id: ID)" — fall back to old format (name only) if not matched
        const newFormatEntry = /^(.+?)\s+\(size:\s*(\d+),\s*id:\s*([^)]+)\)$/;
        let hasOldAttachment = false;
        const attachments = entries
            .filter(Boolean)
            .map((entry) => {
                const entryMatch = entry.match(newFormatEntry);
                if (entryMatch) {
                    return {
                        name: entryMatch[1],
                        size: parseInt(entryMatch[2], 10),
                        fileId: entryMatch[3].trim(),
                    } as Attachment & Pick<IChatAttachmentBindingModel, "fileId">;
                }
                hasOldAttachment = true;
                return { name: entry } as Attachment;
            });

        return { strippedText: message.substring(0, match.index!).trimEnd(), attachments, hasOldAttachment };
    }
}
